import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Eye, EyeOff, LogOut, RefreshCw, Users, Mail, TrendingUp, Clock } from "lucide-react";

const AdminPanel = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");
    const [authError, setAuthError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const [filterStatus, setFilterStatus] = useState("ALL");
    const [filterType, setFilterType] = useState("ALL");

    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

    useEffect(() => {
        setIsAuthenticated(false);
        setLoading(false);
    }, []);

    const handleLogin = (e) => {
        e.preventDefault();
        const correctPassword =
            process.env.REACT_APP_ADMIN_PASSWORD ||
            "Adsurgin@2026";
        if (passwordInput === correctPassword) {
            setIsAuthenticated(true);
            setAuthError("");
        } else {
            setAuthError("Incorrect password. Please try again.");
        }
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        setPasswordInput("");
    };

    const fetchEnquiries = async (isRefresh = false) => {
        if (!isAuthenticated) return;
        isRefresh ? setRefreshing(true) : setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/contacts`);
            const data = await res.json();
            if (data.success) {
                setEnquiries(data.data);
                setError("");
            } else {
                throw new Error(data.message);
            }
        } catch (err) {
            setError(err.message || "Failed to fetch enquiries.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) fetchEnquiries();
    }, [isAuthenticated]);

    const updateStatus = async (id, status) => {
        try {
            const res = await fetch(`${API_BASE}/api/contacts/${id}/status`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status }),
            });
            if (res.ok) fetchEnquiries(true);
        } catch (err) {
            console.error("Error updating status:", err);
        }
    };

    const statusStyles = {
        NEW: "bg-blue-100 text-blue-700 ring-1 ring-blue-200",
        IN_PROGRESS: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
        CLOSED: "bg-gray-100 text-gray-600 ring-1 ring-gray-200",
    };

    const stats = {
        total: enquiries.length,
        newCount: enquiries.filter((e) => e.status === "NEW").length,
        inProgress: enquiries.filter((e) => e.status === "IN_PROGRESS").length,
        closed: enquiries.filter((e) => e.status === "CLOSED").length,
    };

    const filtered = enquiries.filter((e) => {
        const matchStatus = filterStatus === "ALL" || e.status === filterStatus;
        const matchType = filterType === "ALL" || e.type === filterType;
        return matchStatus && matchType;
    });

    // ── LOGIN SCREEN ──────────────────────────────────────────────────────────
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-cyan-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 24, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="bg-white w-full max-w-md rounded-3xl shadow-2xl shadow-gray-200/70 overflow-hidden border border-gray-100"
                >
                    {/* Header */}
                    <div className="relative bg-gradient-to-br from-[#00809D] to-[#005f75] px-8 py-10 text-center overflow-hidden">
                        <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                        <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-48 h-1 bg-white/20 rounded-full blur-sm" />

                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.15, duration: 0.4 }}
                            className="relative z-10 w-18 h-18 mx-auto mb-5"
                        >
                            <div className="w-[72px] h-[72px] bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/30 shadow-lg mx-auto">
                                <Lock className="w-8 h-8 text-white" />
                            </div>
                        </motion.div>

                        <h2 className="text-3xl font-bold text-white relative z-10 tracking-tight">
                            Admin Access
                        </h2>
                        <p className="text-white/70 text-lg">
                            Secured dashboard — authorized personnel only
                        </p>
                    </div>

                    {/* Form */}
                    <div className="p-8">
                        <form onSubmit={handleLogin} className="space-y-5">
                            <div>
                                <label className="block text-gray-700 text-lg">
                                    Master Password
                                </label>
                                <div className="relative group">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={passwordInput}
                                        onChange={(e) => {
                                            setPasswordInput(e.target.value);
                                            setAuthError("");
                                        }}
                                        placeholder="Enter your password"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 pr-12 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#00809D] focus:ring-2 focus:ring-[#00809D]/20 focus:bg-white transition-all duration-200 text-lg" />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#00809D] transition-colors"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-4.5 h-4.5" />
                                        ) : (
                                            <Eye className="w-4.5 h-4.5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <AnimatePresence>
                                {authError && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -6, height: 0 }}
                                        animate={{ opacity: 1, y: 0, height: "auto" }}
                                        exit={{ opacity: 0, y: -6, height: 0 }}
                                        className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-lg">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                                        {authError}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <button
                                type="submit"
                                className="w-full bg-gradient-to-r from-[#00809D] to-[#006d87] hover:from-[#006d87] hover:to-[#005a72] text-white py-3.5 rounded-xl font-bold text-lg">
                                Unlock Dashboard →
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        );
    }

    // ── LOADING SCREEN ────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="relative w-14 h-14">
                        <div className="absolute inset-0 rounded-full border-4 border-[#00809D]/20" />
                        <div className="absolute inset-0 rounded-full border-4 border-t-[#00809D] animate-spin" />
                    </div>
                    <div className="text-center">
                        <p className="text-gray-700 font-semibold">Loading Dashboard</p>
                        <p className="text-gray-400 text-lg">Fetching enquiries...</p>
                    </div>
                </div>
            </div>
        );
    }

    // ── DASHBOARD ─────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50/80">
            {/* Top Bar */}
            <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-200/80 shadow-sm">
                <div className="max-w-[96%] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#00809D] flex items-center justify-center">
                            <Lock className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-gray-900 text-3xl tracking-tight">
                            Admin Panel
                        </span>
                        <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-lg">
                            ● Live
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => fetchEnquiries(true)}
                            disabled={refreshing}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-lg">
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 font-medium rounded-lg text-lg">
                            <LogOut className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[96%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Title */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                        Enquiries & Messages
                    </h1>
                    <p className="text-gray-500 text-lg">
                        Manage all incoming contact messages and campaign enquiries.
                    </p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                        { label: "Total", value: stats.total, icon: Users, color: "text-[#00809D]", bg: "bg-cyan-50" },
                        { label: "New", value: stats.newCount, icon: Mail, color: "text-blue-600", bg: "bg-blue-50" },
                        { label: "In Progress", value: stats.inProgress, icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
                        { label: "Closed", value: stats.closed, icon: Clock, color: "text-gray-500", bg: "bg-gray-100" },
                    ].map((stat) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-gray-500 text-lg">
                                    {stat.label}
                                </span>
                                <div className={`w-8 h-8 ${stat.bg} rounded-lg flex items-center justify-center`}>
                                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                                </div>
                            </div>
                            <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Error Banner */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-red-50 border border-red-100 text-red-600 px-5 py-4 rounded-xl mb-6 text-lg">
                            <span className="w-2 h-2 bg-red-500 rounded-full" />
                            {error}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-4">
                    <div className="px-5 py-4 flex flex-wrap gap-3 items-center border-b border-gray-100">
                        <span className="text-lg">
                            Filter by:
                        </span>

                        {/* Status Filter */}
                        <div className="flex gap-1.5 flex-wrap">
                            {["ALL", "NEW", "IN_PROGRESS", "CLOSED"].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setFilterStatus(s)}
                                    className={`px-3 py-1.5 rounded-lg text-base font-medium transition-all ${filterStatus === s ? 'bg-[#00809D] text-white shadow-md' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                >
                                    {s === "IN_PROGRESS" ? "In Progress" : s.charAt(0) + s.slice(1).toLowerCase().replace("_", " ")}
                                </button>
                            ))}
                        </div>

                        <div className="w-px h-5 bg-gray-200 hidden sm:block" />

                        {/* Type Filter */}
                        <div className="flex gap-1.5">
                            {["ALL", "ENQUIRY", "CONTACT"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => setFilterType(t)}
                                    className={`px-3 py-1.5 rounded-lg text-base font-medium transition-all ${filterType === t ? 'bg-[#00809D] text-white shadow-md' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                >
                                    {t.charAt(0) + t.slice(1).toLowerCase()}
                                </button>
                            ))}
                        </div>

                        <span className="ml-auto text-lg">
                            Showing {filtered.length} of {enquiries.length}
                        </span>
                    </div>

                    {/* Table */}
                    <div className="bg-transparent lg:bg-white lg:rounded-2xl lg:border lg:border-gray-200 lg:shadow-sm">
                        <table className="w-full text-lg block lg:table">
                            <thead className="hidden lg:table-header-group bg-gray-50">
                                <tr className="text-lg text-left text-gray-500 border-b border-gray-200">
                                    <th className="px-6 py-4 font-semibold w-[15%]">Date</th>
                                    <th className="px-6 py-4 font-semibold w-[10%]">Type</th>
                                    <th className="px-6 py-4 font-semibold w-[15%]">Name</th>
                                    <th className="px-6 py-4 font-semibold w-[20%]">Contact</th>
                                    <th className="px-6 py-4 font-semibold w-[25%]">Details</th>
                                    <th className="px-6 py-4 font-semibold w-[15%]">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y-0 lg:divide-y lg:divide-gray-100 block lg:table-row-group">
                                {filtered.length === 0 ? (
                                    <tr className="block lg:table-row bg-white rounded-2xl border border-gray-200 lg:border-none shadow-sm lg:shadow-none">
                                        <td colSpan="6" className="px-6 py-16 text-center block lg:table-cell">
                                            <div className="flex flex-col items-center gap-2 text-gray-400">
                                                <Mail className="w-8 h-8 opacity-40" />
                                                <p className="font-medium text-gray-500">No enquiries found</p>
                                                <p className="text-lg">Try changing filters or refresh the data.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((enquiry, i) => (
                                        <motion.tr
                                            key={enquiry._id}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.03 }}
                                            className="hover:bg-[#00809D]/[0.03] transition-colors group block lg:table-row p-6 lg:p-0 mb-6 lg:mb-0 border border-gray-200 lg:border-0 rounded-2xl lg:rounded-none bg-white lg:bg-transparent shadow-sm lg:shadow-none"
                                        >
                                            {/* Date */}
                                            <td className="px-0 lg:px-6 py-3 lg:py-4 block lg:table-cell border-b border-gray-100 lg:border-none">
                                                <div className="flex justify-between items-center lg:block">
                                                    <span className="lg:hidden font-semibold text-gray-500 text-sm uppercase tracking-wider">Date</span>
                                                    <div className="text-right lg:text-left">
                                                        <p className="text-gray-800 font-medium text-lg">
                                                            {new Date(enquiry.createdAt).toLocaleDateString("en-IN", {
                                                                day: "numeric", month: "short", year: "numeric",
                                                            })}
                                                        </p>
                                                        <p className="text-gray-400 text-base">
                                                            {new Date(enquiry.createdAt).toLocaleTimeString("en-IN", {
                                                                hour: "2-digit", minute: "2-digit",
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Type */}
                                            <td className="px-0 lg:px-6 py-3 lg:py-4 block lg:table-cell border-b border-gray-100 lg:border-none">
                                                <div className="flex justify-between items-center lg:block">
                                                    <span className="lg:hidden font-semibold text-gray-500 text-sm uppercase tracking-wider">Type</span>
                                                    <span className={`px-2.5 py-1 rounded-full text-base font-medium ${enquiry.type === 'ENQUIRY' ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-200' : 'bg-teal-100 text-teal-700 ring-1 ring-teal-200'}`}>
                                                        {enquiry.type}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Name */}
                                            <td className="px-0 lg:px-6 py-3 lg:py-4 block lg:table-cell border-b border-gray-100 lg:border-none">
                                                <div className="flex justify-between items-center lg:block">
                                                    <span className="lg:hidden font-semibold text-gray-500 text-sm uppercase tracking-wider">Name</span>
                                                    <div className="text-right lg:text-left">
                                                        <p className="font-semibold text-gray-900 text-lg">{enquiry.name}</p>
                                                        {enquiry.company && (
                                                            <p className="text-base text-gray-500">{enquiry.company}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Contact */}
                                            <td className="px-0 lg:px-6 py-3 lg:py-4 block lg:table-cell border-b border-gray-100 lg:border-none">
                                                <div className="flex justify-between items-center lg:block">
                                                    <span className="lg:hidden font-semibold text-gray-500 text-sm uppercase tracking-wider">Contact</span>
                                                    <div className="text-right lg:text-left">
                                                        <a
                                                            href={`mailto:${enquiry.email}`}
                                                            className="text-[#00809D] hover:underline text-lg block break-all">
                                                            {enquiry.email}
                                                        </a>
                                                        <p className="text-gray-500 text-base">{enquiry.phone}</p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Details */}
                                            <td className="px-0 lg:px-6 py-3 lg:py-4 block lg:table-cell border-b border-gray-100 lg:border-none lg:max-w-[400px]">
                                                <div className="flex flex-col lg:block mt-2 lg:mt-0">
                                                    <span className="lg:hidden font-semibold text-gray-500 text-sm uppercase tracking-wider mb-2">Details</span>
                                                    {enquiry.type === "ENQUIRY" ? (
                                                        <div className="text-lg bg-gray-50 lg:bg-transparent p-4 lg:p-0 rounded-xl">
                                                            {enquiry.platforms?.length > 0 && (
                                                                <p className="mb-1">
                                                                    <span className="font-semibold text-gray-700">Platforms:</span>{" "}
                                                                    {enquiry.platforms.join(", ")}
                                                                </p>
                                                            )}
                                                            {enquiry.goals?.length > 0 && (
                                                                <p className="mb-1">
                                                                    <span className="font-semibold text-gray-700">Goals:</span>{" "}
                                                                    {enquiry.goals.join(", ")}
                                                                </p>
                                                            )}
                                                            <div className="flex flex-col sm:flex-row sm:gap-4 mt-2 pt-2 border-t border-gray-200 lg:border-none lg:pt-0 lg:mt-0">
                                                                {enquiry.budget && (
                                                                    <p>
                                                                        <span className="font-semibold text-gray-700">Budget:</span>{" "}
                                                                        {enquiry.budget}
                                                                    </p>
                                                                )}
                                                                {enquiry.timeline && (
                                                                    <p>
                                                                        <span className="font-semibold text-gray-700">Timeline:</span>{" "}
                                                                        {enquiry.timeline}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-lg bg-gray-50 lg:bg-transparent p-4 lg:p-0 rounded-xl">
                                                            <p className="mb-1">
                                                                <span className="font-semibold text-gray-700">Subject:</span>{" "}
                                                                {enquiry.subject}
                                                            </p>
                                                            <p className="line-clamp-4 lg:line-clamp-2 text-gray-600 italic leading-relaxed text-base">
                                                                "{enquiry.message}"
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="px-0 lg:px-6 py-4 block lg:table-cell">
                                                <div className="flex justify-between items-center lg:block">
                                                    <span className="lg:hidden font-semibold text-gray-500 text-sm uppercase tracking-wider">Status</span>
                                                    <select
                                                        value={enquiry.status}
                                                        onChange={(e) => updateStatus(enquiry._id, e.target.value)}
                                                        className={`px-3 py-2.5 lg:py-1.5 rounded-lg text-lg font-medium transition-all border-0 focus:ring-2 focus:ring-[#00809D]/20 cursor-pointer w-1/2 sm:w-auto lg:w-auto text-right lg:text-left ${statusStyles[enquiry.status]}`}>
                                                        <option value="NEW">🔵 New</option>
                                                        <option value="IN_PROGRESS">🟡 In Progress</option>
                                                        <option value="CLOSED">⚫ Closed</option>
                                                    </select>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {filtered.length > 0 && (
                        <div className="px-6 py-3.5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
                            <p className="text-lg">
                                {filtered.length} record{filtered.length !== 1 ? "s" : ""} displayed
                            </p>
                            <p className="text-lg">
                                Last updated: {new Date().toLocaleTimeString("en-IN")}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPanel;
